import cn from 'classnames';
import { useCallback, useRef } from 'preact/hooks';
import { Checkbox, RadioButton } from '../form-field';
import { MenuList } from '../list';
import { EMPTY_ARRAY } from '../../utility';
import type { ComponentChildren, ComponentProps, TargetedEvent } from 'preact';
import type { HTMLAttributes } from '../../types/utility';
import type { CheckableProps, Option } from '../form-field';

type VisibilityType = 'visible' | 'visually-hidden' | 'visually-maintain-layout';

type FieldsetProps = Omit<HTMLAttributes<HTMLFieldSetElement>, 'elements'> & {
  disabled?: boolean;
  errorMessage?: ComponentChildren;
  hintMessage?: ComponentChildren;
  legend: ComponentChildren;
  legendVisibility?: VisibilityType;
  /**
   * @deprecated Use `legendVisibility` instead
   */
  isLegendVisible?: boolean;
  name?: string;
  required?: boolean;
};

interface CheckableListProps {
  orientation?: 'horizontal' | 'vertical';
  type: 'checkbox' | 'radio';
  /**
   * @deprecated Use `labelVisibility` instead
   */
  isLabelVisible?: boolean;
  labelVisibility?: VisibilityType;
  options: Option[];
}

export default function Fieldset({
  legend,
  name,
  children,
  disabled,
  errorMessage,
  hintMessage,
  isLegendVisible = true,
  legendVisibility = 'visible',
  required,
  ...props
}: FieldsetProps & {
  errorMessage?: ComponentChildren;
  hintMessage?: ComponentChildren;
  legend: ComponentChildren;
}) {
  const classList =
    cn({
      'visually-hidden': !isLegendVisible,
      [legendVisibility]: legendVisibility !== 'visible',
    }) || undefined;

  return (
    <fieldset disabled={disabled} data-required={required ? name : undefined} {...props}>
      <legend className={classList}>{legend}</legend>
      {hintMessage && <small className="hint-message">{hintMessage}</small>}
      {children}
      {errorMessage && <small className="error-message">{errorMessage}</small>}
    </fieldset>
  );
}

function CheckableList({
  defaultValue = EMPTY_ARRAY as unknown as [],
  isLabelVisible,
  labelVisibility,
  label,
  options = [],
  orientation = 'vertical',
  required,
  type,
  ...props
}: Omit<CheckableProps, 'options'> & CheckableListProps) {
  const defaultValues = useRef(Array.isArray(defaultValue) ? defaultValue : [defaultValue]);

  const children = (
    <MenuList
      orientation={orientation}
      className="checkable"
      items={options.map(({ label, ...option }, index) => {
        const defaultChecked =
          option.defaultChecked || defaultValues.current.includes(option.value as string);
        return type === 'checkbox' ? (
          <Checkbox
            key={option.value || (label as string)}
            label={label as string}
            name={props.name}
            hintMessage={option.subtext}
            {...option}
            defaultChecked={defaultChecked}
          />
        ) : (
          <RadioButton
            key={option.value || (label as string)}
            label={label as string}
            name={props.name}
            hintMessage={option.subtext}
            required={required && index === 0}
            {...option}
            defaultChecked={defaultChecked}
          />
        );
      })}
    />
  );

  return (
    <>
      {/* @ts-ignore Property 'elements' is missing in type 'HTMLInputElement' but required in type 'HTMLFieldSetElement'.ts(2322) */}
      <Fieldset
        legend={label}
        required={type === 'checkbox' && required}
        isLegendVisible={isLabelVisible}
        legendVisibility={labelVisibility}
        {...props}
      >
        {children}
      </Fieldset>
    </>
  );
}

const handleValidateCheckboxList = (e: TargetedEvent<HTMLFieldSetElement>) => {
  const { currentTarget } = e;

  if (!currentTarget.dataset.required) {
    return;
  }

  const firstChecked = currentTarget.querySelector(':checked') as HTMLInputElement | null;

  for (const checkbox of currentTarget.elements) {
    (checkbox as HTMLInputElement).setCustomValidity(
      firstChecked && firstChecked.value ? '' : 'Please select a value',
    );
  }
};

export function CheckboxList({
  className,
  onChange,
  ...props
}: Omit<ComponentProps<typeof CheckableList>, 'type'>) {
  const classList = cn('checkbox-list', className);

  const handleChange = useCallback(
    (e: Event) => {
      handleValidateCheckboxList(e as TargetedEvent<HTMLFieldSetElement>);
      onChange?.(e as unknown as TargetedEvent<HTMLInputElement>);
    },
    [onChange],
  );

  return <CheckableList type="checkbox" className={classList} onChange={handleChange} {...props} />;
}

export function RadioButtonList({
  className,
  type = 'unbounded',
  size = 'medium',
  ...props
}: Omit<ComponentProps<typeof CheckableList>, 'type' | 'size'> & {
  type?: 'unbounded' | 'bounded-separated';
  size?: 'small' | 'medium';
}) {
  const classList = cn('radio-button-list', className, {
    [type]: type !== 'unbounded',
    [`size-${size}`]: size !== 'medium',
  });

  return <CheckableList type="radio" className={classList} {...props} />;
}
